import { supabase } from '@/lib/supabase';
import { Audio, ResizeMode, Video } from 'expo-av';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeft, Play, WifiSlash } from 'phosphor-react-native';
import { useEffect, useRef, useState } from 'react';
import { Dimensions, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

const { width } = Dimensions.get('window');

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  emerald600: '#059669',
  emerald500: '#10B981',
};

interface VideoData {
  id: string;
  title: string;
  matiere: string;
  duration: string | null;
  thumbnail_url: string | null;
  video_url: string;
  theme_id: string | null;
  sous_matiere: string | null;
  chapitre_id: string | null;
  ordre: number;
  is_free: boolean;
}

export default function VideoViewerScreen() {
  const { id } = useLocalSearchParams();
  const videoRef = useRef<Video>(null);
  const [video, setVideo] = useState<VideoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    loadVideoFromSupabase();
  }, [id]);

  // ✅ Force le son même en mode silencieux iOS
  useEffect(() => {
    Audio.setAudioModeAsync({
      playsInSilentModeIOS: true,
      staysActiveInBackground: false,
      shouldDuckAndroid: true,
    });
  }, []);

  const loadVideoFromSupabase = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('prof_kylian_videos')
        .select('*')
        .eq('id', id as string)
        .single();

      if (fetchError || !data) {
        console.error('Erreur fetch vidéo:', fetchError);
        setError('Vidéo introuvable');
        setLoading(false);
        return;
      }

      setVideo(data as VideoData);
      setLoading(false);
    } catch (err) {
      console.error('Erreur chargement vidéo:', err);
      setError('Impossible de charger la vidéo.');
      setLoading(false);
    }
  };

  const handlePlayPress = async () => {
    if (videoRef.current) {
      await videoRef.current.playAsync();
    }
  };

  // État de chargement
  if (loading) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.loadingContainer]}>
          <ActivityIndicator size="large" color={colors.indigo600} />
          <Text style={styles.loadingText}>Chargement de la vidéo...</Text>
        </View>
      </>
    );
  }

  // État d'erreur
  if (error || !video) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.errorContainer]}>
          <WifiSlash size={64} color={colors.slate300} weight="duotone" />
          <Text style={styles.errorTitle}>{error || 'Vidéo introuvable'}</Text>
          <Text style={styles.errorSubtitle}>
            Vérifie ta connexion internet et réessaie.
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadVideoFromSupabase}>
            <Text style={styles.retryButtonText}>Réessayer</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.errorButton} onPress={() => router.back()}>
            <Text style={styles.errorButtonText}>Retour</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />

      <View style={styles.container}>
        {/* Header */}
        <LinearGradient
          colors={[colors.indigo700, colors.indigo600]}
          style={styles.header}
        >
          <View style={styles.headerContent}>
            <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
              <ArrowLeft size={24} color={colors.white} weight="bold" />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle} numberOfLines={2}>{video.title}</Text>
              <Text style={styles.headerChapter}>
                {video.matiere} {video.sous_matiere ? `• ${video.sous_matiere}` : ''}
                {video.duration ? ` • ${video.duration}` : ''}
              </Text>
            </View>
          </View>
        </LinearGradient>

        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          {/* Lecteur vidéo */}
          <View style={styles.videoContainer}>
            {/* Loader pendant le chargement */}
            {videoLoading && (
              <View style={styles.videoLoader}>
                <ActivityIndicator size="large" color={colors.white} />
                <Text style={styles.videoLoaderText}>Chargement de la vidéo...</Text>
              </View>
            )}
            
            {/* Vidéo */}
            <Video
              ref={videoRef}
              source={{ uri: video.video_url }}
              posterSource={video.thumbnail_url ? { uri: video.thumbnail_url } : undefined}
              usePoster={!!video.thumbnail_url}
              posterStyle={styles.poster}
              style={styles.video}
              useNativeControls
              resizeMode={ResizeMode.CONTAIN}
              onLoadStart={() => setVideoLoading(true)}
              onLoad={() => setVideoLoading(false)}
              onPlaybackStatusUpdate={(status) => {
                if (status.isLoaded) {
                  setIsPlaying(status.isPlaying);
                }
              }}
              onError={(err) => {
                console.error('Erreur vidéo:', err);
                setVideoLoading(false);
                setError('Impossible de lire la vidéo.');
              }}
            />

            {/* Bouton Play custom - visible uniquement si pas en lecture et pas en chargement */}
            {!isPlaying && !videoLoading && (
              <TouchableOpacity
                style={styles.customPlayButton}
                onPress={handlePlayPress}
                activeOpacity={0.9}
              >
                <View style={styles.playButtonCircle}>
                  <Play size={36} color={colors.white} weight="fill" style={{ marginLeft: 4 }} />
                </View>
                <Text style={styles.playButtonText}>Lancer la vidéo</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Infos sous la vidéo */}
          <View style={styles.infoSection}>
            <Text style={styles.videoTitle}>{video.title}</Text>
            <View style={styles.metaRow}>
              <View style={styles.metaBadge}>
                <Text style={styles.metaBadgeText}>{video.matiere}</Text>
              </View>
              {video.sous_matiere && (
                <View style={[styles.metaBadge, styles.metaBadgeSecondary]}>
                  <Text style={[styles.metaBadgeText, styles.metaBadgeTextSecondary]}>
                    {video.sous_matiere}
                  </Text>
                </View>
              )}
              {video.duration && (
                <View style={styles.durationContainer}>
                  <Play size={14} color={colors.slate500} weight="fill" />
                  <Text style={styles.durationText}>{video.duration}</Text>
                </View>
              )}
            </View>
          </View>

          <View style={{ height: 40 }} />
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },

  // Header
  header: {
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 16,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  headerChapter: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 4,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 15,
    color: colors.slate500,
  },

  // Scroll
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 20,
  },

  // Vidéo
  videoContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#0f172a',
    position: 'relative',
  },
  video: {
    width: '100%',
    height: '100%',
  },
  poster: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  videoLoader: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(15, 23, 42, 0.9)',
    zIndex: 2,
  },
  videoLoaderText: {
    marginTop: 12,
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
  },

  // Bouton Play custom
  customPlayButton: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    zIndex: 1,
  },
  playButtonCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.indigo600,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 10,
    elevation: 10,
  },
  playButtonText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },

  // Infos sous la vidéo
  infoSection: {
    padding: 20,
  },
  videoTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.slate900,
    lineHeight: 30,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  metaBadge: {
    backgroundColor: colors.indigo100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  metaBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.indigo700,
  },
  metaBadgeSecondary: {
    backgroundColor: colors.slate100,
  },
  metaBadgeTextSecondary: {
    color: colors.slate600,
  },
  durationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  durationText: {
    fontSize: 13,
    color: colors.slate500,
    fontWeight: '500',
  },

  // Error state
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    marginTop: 20,
  },
  errorSubtitle: {
    fontSize: 15,
    color: colors.slate500,
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 24,
    backgroundColor: colors.indigo600,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  retryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  errorButton: {
    marginTop: 12,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  errorButtonText: {
    color: colors.slate500,
    fontSize: 16,
    fontWeight: '600',
  },
});